/**
 * Helpers for building multi-practice query strings.
 *
 * Phase 1 of the "All Practices" rollout generalises the `?practice__in=`
 * pattern that started in the reporting surface (see
 * apps/reports/mixins.py::resolve_report_practices) so the rest of the
 * app can adopt the same aggregation without each caller reinventing the
 * wheel.
 *
 * The normal API URL pattern in this codebase is
 * `/api/v1/practices/{practice_pk}/<resource>/` — i.e. the primary
 * practice is already in the URL path. When the user has selected
 * multiple practices we send the full list as `?practice__in=1,2,3` and
 * the backend resolves the actual scope from that list (validating user
 * access per id). Single-practice selections need no suffix — the URL's
 * `practice_pk` is enough.
 *
 * Why not just always send `practice__in`? Two reasons:
 *   1. Adding the param forces the backend into the multi-practice code
 *      path, which calls `resolve_report_practices` and hits the DB for
 *      the access check. Cheap but non-zero cost on every request.
 *   2. Not every endpoint supports `practice__in` yet — phases 2-3 of
 *      this rollout migrate them one app at a time. In the meantime
 *      sending the param to an endpoint that doesn't understand it is
 *      a silent no-op (the param is just ignored), so the suffix is a
 *      safe-to-apply opt-in that graduates cleanly as backend catches up.
 */

/**
 * Normalise input to an array of integer-ish practice IDs.
 * Accepts `null`, a single id (string or number), or an array.
 * Returns [] for empty input so callers can early-return.
 */
export const toPracticeIds = (practiceIds) => {
    if (Array.isArray(practiceIds)) {
        return practiceIds.filter((id) => id != null && id !== '');
    }
    if (practiceIds != null && practiceIds !== '') return [practiceIds];
    return [];
};

/**
 * Return `&practice__in=1,2,3` when the caller has selected MULTIPLE
 * practices, or the empty string otherwise.
 *
 * The single-practice case intentionally returns '' — the existing
 * `/api/v1/practices/{id}/...` URL already scopes to that practice and
 * there's no point round-tripping the same id through a query param.
 * Backends that haven't been taught `practice__in` yet will ignore it
 * safely; backends that have will use it in preference to the URL's
 * `practice_pk`.
 *
 * Append directly to an existing query string:
 *   `?foo=1${practiceInSuffix(ids)}`
 */
export const practiceInSuffix = (ids) => {
    const list = toPracticeIds(ids);
    return list.length > 1 ? `&practice__in=${list.join(',')}` : '';
};

/**
 * Same as `practiceInSuffix` but without the leading `&` — use when the
 * caller is starting a fresh query string:
 *   `?${practiceInQueryStart(ids)}` (returns `practice__in=1,2,3` or '')
 *
 * Rarely needed in this codebase because every endpoint path is nested
 * under `/practices/{id}/...` so the URL-path approach is usually the
 * right level of nesting. Kept for the handful of top-level endpoints
 * that take a full query string (e.g. `/api/v1/leads-flat/`).
 */
export const practiceInQueryStart = (ids) => {
    const list = toPracticeIds(ids);
    return list.length > 1 ? `practice__in=${list.join(',')}` : '';
};

/**
 * Pick the primary practice id from a multi-practice selection.
 *
 * URLs still need ONE id in the path — convention is "the first one in
 * the selection", which matches what the analytics tabs already do.
 * Falls back to `null` when nothing is selected so callers can bail
 * (the real `/practices/{id}/...` URL would 404 without it anyway).
 */
export const primaryPracticeId = (ids) => {
    const list = toPracticeIds(ids);
    return list[0] ?? null;
};
